/**
 * Telemetría de los coach-marks (lo que pasa en pantalla durante un tour).
 *
 * Antes escribía en una tabla `onboarding_tour_events` que **no existe en
 * ninguna de las dos bases**: el SQL quedó sin aplicar, así que todos los
 * inserts fallaban en silencio y los tours por módulo nunca reportaron nada.
 * Además exigía `organizationId` para intentar el insert, descartando los
 * eventos de los primeros minutos de vida de una cuenta — justo los que
 * interesan para medir activación.
 *
 * Ahora es una fachada delgada sobre `onboardingTelemetry` → tabla
 * `onboarding_events`, que sí existe, tiene RLS (`user_id = auth.uid()` o la
 * org del usuario) y alimenta la vista de funnel del panel superadmin. Una sola
 * tabla de eventos de onboarding, no dos.
 *
 * División de responsabilidades: `onboardingEngine` reporta las transiciones de
 * estado del recorrido; este archivo reporta lo que ocurre en el render (paso
 * visto, usuario trabado, paso salteado). Los eventos llevan
 * `metadata.source = 'ui'` para poder distinguirlos.
 */

import * as Sentry from '@sentry/react';
import { logOnboardingEvent } from './onboardingTelemetry';

// Cuántas veces se corrió un tour en esta sesión/dispositivo. Sirve para
// separar el primer intento de las repeticiones desde la academia.
const getTourRunCount = (tourId) => {
  try {
    const value = parseInt(sessionStorage.getItem(`gst_tour_runs_${tourId}`) || '0', 10);
    return Number.isNaN(value) ? 0 : value;
  } catch {
    return 0;
  }
};

const incrementTourRunCount = (tourId) => {
  try {
    const next = getTourRunCount(tourId) + 1;
    sessionStorage.setItem(`gst_tour_runs_${tourId}`, String(next));
    return next;
  } catch {
    return 1;
  }
};

/**
 * Envía un evento de tour a `onboarding_events` y deja un breadcrumb en Sentry.
 * Asíncrono y resiliente: nunca interrumpe ni bloquea el recorrido.
 */
async function sendTourEvent({
  tourId,
  event,
  stepIndex = 0,
  timeSpentMs = 0,
  organizationId = null,
  userId = null,
  metadata = {},
}) {
  const detail = {
    ...metadata,
    source: 'ui',
    tourId,
    stepIndex,
    timeSpentMs,
    runCount: getTourRunCount(tourId),
  };

  try {
    Sentry.addBreadcrumb({
      category: 'onboarding.tour',
      message: `[Tour:${tourId}] ${event} (paso ${stepIndex})`,
      level: event === 'tour_stuck' ? 'warning' : 'info',
      data: detail,
    });
  } catch {
    // Sentry no disponible o deshabilitado.
  }

  // `step_id` queda legible en la tabla: "load-catalog:2".
  return logOnboardingEvent({
    event,
    stepId: `${tourId}:${stepIndex}`,
    organizationId,
    userId,
    metadata: detail,
  });
}

/** El usuario inicia (o reinicia desde la academia) un tour interactivo. */
export async function trackTourStarted({ tourId, organizationId, userId, stepCount }) {
  const runCount = incrementTourRunCount(tourId);
  return sendTourEvent({
    tourId,
    event: 'tour_started',
    stepIndex: 0,
    organizationId,
    userId,
    metadata: { stepCount, runCount },
  });
}

/** Se mostró un paso del tour en pantalla. */
export async function trackTourStepViewed({ tourId, stepIndex, stepTitle, selector, organizationId, userId }) {
  return sendTourEvent({
    tourId,
    event: 'tour_step_viewed',
    stepIndex,
    organizationId,
    userId,
    metadata: { stepTitle, selector },
  });
}

/** El usuario completó un paso (con tiempo y forma de avance). */
export async function trackTourStepCompleted({ tourId, stepIndex, timeSpentMs, autoAdvanced = false, organizationId, userId }) {
  return sendTourEvent({
    tourId,
    event: 'tour_step_completed',
    stepIndex,
    timeSpentMs,
    organizationId,
    userId,
    metadata: { autoAdvanced },
  });
}

/**
 * El usuario quedó trabado: el selector del paso no apareció, hubo reintentos
 * o inactividad. Es la señal que explica dónde se cae el funnel, así que
 * además se reporta a Sentry cuando se repite.
 */
export async function trackTourStuck({ tourId, stepIndex, stepTitle, reason, attemptsCount = 1, organizationId, userId }) {
  try {
    if (attemptsCount >= 2) {
      Sentry.captureMessage(`[Onboarding] Usuario trabado en tour ${tourId}, paso ${stepIndex} (${stepTitle})`, {
        level: 'warning',
        extra: { tourId, stepIndex, stepTitle, reason, attemptsCount, organizationId },
      });
    }
  } catch {
    // Sentry no disponible.
  }

  return sendTourEvent({
    tourId,
    event: 'tour_stuck',
    stepIndex,
    organizationId,
    userId,
    metadata: { stepTitle, reason, attemptsCount },
  });
}

/** El usuario salteó un paso o cerró el tour antes de terminarlo. */
export async function trackTourSkipped({ tourId, stepIndex, organizationId, userId }) {
  return sendTourEvent({
    tourId,
    event: 'tour_step_skipped',
    stepIndex,
    organizationId,
    userId,
  });
}

/** El tour terminó completo. */
export async function trackTourCompleted({ tourId, totalTimeSpentMs, stepsCompletedCount, organizationId, userId }) {
  return sendTourEvent({
    tourId,
    event: 'tour_completed',
    stepIndex: Math.max(0, (stepsCompletedCount || 1) - 1),
    timeSpentMs: totalTimeSpentMs,
    organizationId,
    userId,
    metadata: { totalTimeSpentMs, stepsCompletedCount },
  });
}
